import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import { duplicatePaperFromError } from "../../api/duplicatePaper";
import type { DuplicatePaperRef, Supplier } from "../../api/types";
import { paperUseLabel } from "../../utils/paperUse";

export interface PaperRead {
  invoiceId: string;
  supplierName: string;
  /** The paper's number when the reader found one, else the file name. */
  label: string;
}

/**
 * G-129 (OD-160) — "Read a paper for items only", on the staging page
 * itself. Supplier + PDF, nothing else: the paper is filed as ITEMS_ONLY and
 * the server picks the shop (items are global, OD-37; the shop would only be
 * noise here — the general upload page keeps its picker, OD-38). The page
 * waits here while the server reads the paper and creates its items, then
 * reloads the queue.
 */
export function ItemsOnlyUploadBox({ onRead }: { onRead: (read: PaperRead) => void | Promise<void> }) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  // The poll below must not restart whenever the parent re-renders with a new
  // callback identity — the latest callback is read through a ref instead.
  const onReadRef = useRef(onRead);
  useEffect(() => {
    onReadRef.current = onRead;
  }, [onRead]);
  const [supplierId, setSupplierId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState<{ invoiceId: string; supplierName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicatePaperRef | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api
      .listSuppliers()
      .then((rows) => setSuppliers(rows.filter((s) => s.isActive && s.hasParser)))
      .catch(() => setSuppliers([]));
  }, []);

  // The read runs on the server (a cron every 10 s, then the items). Poll the
  // paper every 3 s, the way its own page does, until it is read or failed.
  useEffect(() => {
    if (!reading) return;
    let cancelled = false;
    const t = window.setInterval(() => {
      void api
        .getInvoice(reading.invoiceId)
        .then((inv) => {
          if (cancelled) return;
          if (inv.status === "UPLOADED" || inv.status === "PARSING") return;
          setReading(null);
          if (inv.status === "FAILED") {
            setError(inv.parseError ?? "The paper could not be read.");
            return;
          }
          void onReadRef.current({
            invoiceId: inv.id,
            supplierName: reading.supplierName,
            label: inv.invoiceNumber ?? inv.originalFilename,
          });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setReading(null);
          setError(err instanceof Error ? err.message : "Lost sight of the paper being read.");
        });
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [reading]);

  const submit = async () => {
    setError(null);
    setDuplicate(null);
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) {
      setError("Pick the supplier that issued the PDF");
      return;
    }
    if (!file) {
      setError("Choose a PDF");
      return;
    }
    if (file.type && file.type !== "application/pdf") {
      setError("Only PDF papers can be read");
      return;
    }
    setBusy(true);
    try {
      const created = await api.uploadInvoice({ file, supplierId, paperUse: "ITEMS_ONLY" });
      setReading({ invoiceId: created.id, supplierName: supplier.name });
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
    } catch (err: unknown) {
      const twin = duplicatePaperFromError(err);
      if (twin) {
        setDuplicate(twin);
        return;
      }
      setError(err instanceof Error ? err.message : "Could not upload the paper");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card items-only-upload">
      <div>
        <h3 className="inv-section-title">Read a paper for items only</h3>
        <p className="muted inv-section-note">
          Items the reader knows the category of are created at once; the rest land in the queue
          below. Nothing is counted as stock or money — for a delivery, upload the paper from
          Deliveries.
        </p>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {duplicate ? (
        <div className="alert warning" role="alert">
          {duplicate.canOpen ? (
            <>
              This exact file is already in the app: {paperUseLabel(duplicate.paperUse)}
              {duplicate.invoiceNumber ? ` ${duplicate.invoiceNumber}` : ""}, uploaded{" "}
              {duplicate.createdAt.slice(0, 10)}.{" "}
              <Link to={`/app/invoices/${duplicate.id}`} className="alert-link">
                Open it
              </Link>
            </>
          ) : (
            "This exact file is already in the app, for a shop you cannot open. Ask the owner."
          )}
        </div>
      ) : null}
      {reading ? (
        <p role="status" className="muted inv-section-note">
          Reading the {reading.supplierName} paper and creating its items on the server… this box
          refreshes every few seconds.
        </p>
      ) : (
        <div className="items-only-upload__form">
          <div className="field">
            <label htmlFor="items-only-supplier">Supplier</label>
            <select
              id="items-only-supplier"
              value={supplierId}
              disabled={busy}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">Select supplier…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="items-only-file">PDF</label>
            <input
              id="items-only-file"
              ref={fileInput}
              type="file"
              accept="application/pdf"
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="field items-only-upload__submit">
            <button type="button" className="button" disabled={busy} onClick={() => void submit()}>
              {busy ? "Uploading…" : "Read paper for items"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
